import type { CSSProperties } from 'react';
import { webApp } from './telegram';

/**
 * The documents and the contact, in one place because three screens need them.
 *
 * A payment provider, a bank and a customer all have to be able to reach the
 * same two pages before money changes hands, so the links belong wherever the
 * customer is deciding to pay — not only at the bottom of the home screen.
 *
 * Read from `import.meta.env`, which Vite substitutes at build time. That
 * matters for more than configuration: the values are constants for the life of
 * the bundle, so a link that renders is a link that was there in the first
 * painted frame. Nothing here can appear late and move the page, which is the
 * property `checkout-layout.test.ts` exists to protect.
 */

/**
 * A configured URL, or nothing.
 *
 * `null` rather than a fallback address, and that is the whole point of this
 * module. There used to be two `telegra.ph` addresses written into the source
 * as defaults: a link that is always rendered and only sometimes real. Nobody
 * finds out it is dead until a customer taps it — or worse, until the reviewer
 * who is deciding whether to accept this store does.
 *
 * An empty string is treated as unset, because that is what an environment
 * variable declared and never filled in looks like, and `href=""` is a link
 * back to the current page.
 */
const configuredUrl = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;

  /*
   * Only what a browser can be sent to. `openLink` hands the string to the
   * Telegram client, and a `javascript:` or `data:` value in a build variable
   * is not a link — it is a script the store would be running on itself.
   */
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? trimmed : null;
  } catch {
    return null;
  }
};

/** The privacy policy, when the owner has published one and said where. */
export const PRIVACY_URL = configuredUrl(import.meta.env.VITE_PRIVACY_URL);

/** The public offer — the terms of sale — on the same footing. */
export const TERMS_URL = configuredUrl(import.meta.env.VITE_TERMS_URL);

/**
 * Where a customer with a problem goes, and it is never nothing.
 *
 * Unlike the two documents, this has a default: an unreachable support contact
 * is a worse failure than an unpublished policy, and this one address is known.
 * The variable exists so a store that runs its support elsewhere can say so.
 */
export const SUPPORT_URL =
  configuredUrl(import.meta.env.VITE_SUPPORT_URL) ?? 'https://t.me/mashvrf';

/** The same contact as a handle, for prose that names it rather than links it. */
export const SUPPORT_HANDLE = (() => {
  const match = /t\.me\/([A-Za-z0-9_]+)/.exec(SUPPORT_URL);
  return match ? `@${match[1]}` : null;
})();

/** Whether there is anything to show at all — used to drop an empty container. */
export const hasLegalLinks = PRIVACY_URL !== null || TERMS_URL !== null;

/**
 * Opens a page that is not part of the shop.
 *
 * Inside Telegram a link has to go through `openLink`: the WebView has no tab to
 * open one in, so an ordinary `target="_blank"` is swallowed and the customer
 * taps «Политика конфиденциальности» and watches nothing happen. `openLink` is
 * Bot API 6.0 — the floor for a Mini App at all — so there is no version to gate
 * on here, unlike the calls in `telegram.ts` that carry one.
 *
 * `window.open` is the browser path: this screen has to keep working outside
 * Telegram during development, and a client that advertises the method and
 * throws anyway must still open the document rather than fail silently.
 */
export function openExternal(url: string): void {
  const app = webApp();

  if (app) {
    try {
      app.openLink(url);
      return;
    } catch {
      // Fall through to the browser path below.
    }
  }

  window.open(url, '_blank', 'noopener');
}

/* Quiet, and legible, and not the browser's default blue — nothing in this
   palette is a colour, and an unstyled `<a>` is the one element that would be.
   The underline stays: it is the only thing left saying these are links. */
const LEGAL_LINK: CSSProperties = {
  color: 'var(--text-tertiary)',
  textDecoration: 'underline',
  textUnderlineOffset: '2px',
};

/** The row the links sit in, wherever they sit. */
export const LEGAL_ROW: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  columnGap: 'var(--space-4)',
  rowGap: 'var(--space-2)',
  textAlign: 'center',
};

/**
 * A legal document, opened the Telegram way.
 *
 * A real `<a href>` and not a button: the address is the thing being offered,
 * so it has to survive a long-press, a copy, and a customer who wants to read it
 * outside the shop. The click is intercepted only to route it through Telegram's
 * opener — `target="_blank"` stays as the fallback for a plain browser, and
 * `noreferrer` keeps the shop's own address away from whoever hosts the page.
 */
export function LegalLink({ href, children }: { href: string; children: string }) {
  return (
    <a
      className="caption"
      style={LEGAL_LINK}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => {
        event.preventDefault();
        openExternal(href);
      }}
    >
      {children}
    </a>
  );
}

/**
 * The documents, as a row, or nothing at all.
 *
 * One component so the three screens that show them cannot drift into showing
 * different sets or different words. Renders `null` when neither address is
 * configured, rather than an empty flex row that still occupies its gap.
 */
export function LegalLinks() {
  if (!hasLegalLinks) return null;

  return (
    <div style={LEGAL_ROW}>
      {PRIVACY_URL ? <LegalLink href={PRIVACY_URL}>Политика конфиденциальности</LegalLink> : null}
      {TERMS_URL ? <LegalLink href={TERMS_URL}>Пользовательское соглашение</LegalLink> : null}
    </div>
  );
}
